import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  User,
  Mail,
  Phone,
  FileText,
  Video,
  MapPin,
  AlertTriangle,
  Link,
  RefreshCw,
  Pencil
} from 'lucide-react';
import { format, addMinutes } from 'date-fns';
import { fr, es, enUS } from 'date-fns/locale';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';

import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import RecentInvitations from './RecentInvitations';
import PromptModal from '../modals/PromptModal';
import usePrompt from '../../hooks/usePrompt';
import { parseLocalDate } from '../../utils/dateUtils';
import { appointmentsAPI, handleApiError } from '../../utils/api';

const normalizeTime = (timeValue) => {
  if (!timeValue) return '00:00';
  return String(timeValue).slice(0, 5);
};

const getStatusBadgeClasses = (status) => {
  switch (status) {
    case 'confirmed':
      return 'bg-green-100 text-green-800';
    case 'pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'cancelled':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getEventPalette = (status) => {
  switch (status) {
    case 'confirmed':
      return {
        backgroundColor: '#dcfce7',
        borderColor: '#22c55e',
        textColor: '#166534'
      };
    case 'pending':
      return {
        backgroundColor: '#fef9c3',
        borderColor: '#f59e0b',
        textColor: '#854d0e'
      };
    case 'cancelled':
      return {
        backgroundColor: '#fee2e2',
        borderColor: '#ef4444',
        textColor: '#991b1b'
      };
    default:
      return {
        backgroundColor: '#e0e7ff',
        borderColor: '#6366f1',
        textColor: '#3730a3'
      };
  }
};

const MyCalendar = ({ showToast }) => {
  const { t, currentLanguage } = useLanguage();
  const { user } = useAuth();
  const { prompt, promptProps } = usePrompt();
  const calendarRef = useRef(null);

  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calendarTitle, setCalendarTitle] = useState('');
  const [calendarView, setCalendarView] = useState('timeGridWeek');
  const [calendarRange, setCalendarRange] = useState({ from: null, to: null });

  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [confirmingId, setConfirmingId] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);
  const [reschedulingId, setReschedulingId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [editDraft, setEditDraft] = useState({
    subject: '',
    date: '',
    start_time: '',
    end_time: '',
    meeting_type: 'online',
    meeting_url: ''
  });

  const [videoSettings, setVideoSettings] = useState(null);
  const [bookingUrl, setBookingUrl] = useState('');
  const [manualMeetingUrl, setManualMeetingUrl] = useState('');
  const [showManualUrlInput, setShowManualUrlInput] = useState(false);

  const [userFilter, setUserFilter] = useState('all');
  const [recruiterOptions, setRecruiterOptions] = useState([]);

  const calendarLocale = useMemo(() => {
    if (currentLanguage === 'es') return 'es';
    if (currentLanguage === 'en') return 'en-gb';
    return 'fr';
  }, [currentLanguage]);

  const dateLocale = useMemo(() => {
    if (currentLanguage === 'es') return es;
    if (currentLanguage === 'en') return enUS;
    return fr;
  }, [currentLanguage]);

  const eventList = useMemo(
    () =>
      appointments.map((appointment) => {
        const start = `${appointment.date}T${normalizeTime(appointment.start_time)}:00`;
        const end = `${appointment.date}T${normalizeTime(appointment.end_time)}:00`;
        const palette = getEventPalette(appointment.status);

        return {
          id: String(appointment.id),
          title: appointment.candidate_name || t('appointments.calendar.unknownCandidate', 'Candidat'),
          start,
          end,
          backgroundColor: palette.backgroundColor,
          borderColor: palette.borderColor,
          textColor: palette.textColor,
          extendedProps: {
            raw: appointment
          }
        };
      }),
    [appointments, t]
  );

  const refreshRecruiterOptions = useCallback((list) => {
    if (user?.role !== 'admin') return;

    const map = new Map();
    list.forEach((item) => {
      if (!item.recruiter_id && !item.user_name) return;
      const key = String(item.recruiter_id || item.user_name);
      if (!map.has(key)) {
        map.set(key, {
          id: String(item.recruiter_id || ''),
          name: item.user_name || item.recruiter_name || t('common.unknown', 'Inconnu')
        });
      }
    });

    setRecruiterOptions(Array.from(map.values()));
  }, [t, user?.role]);

  const fetchAppointments = useCallback(async () => {
    if (!calendarRange.from || !calendarRange.to) {
      return;
    }

    try {
      setLoading(true);
      const params = {
        from_date: calendarRange.from,
        to_date: calendarRange.to,
        limit: 2000,
        offset: 0
      };

      if (user?.role === 'admin' && userFilter !== 'all') {
        params.user_filter = userFilter;
      }

      const response = await appointmentsAPI.getCalendarAppointments(params);
      const payload = response.data;
      const list = Array.isArray(payload) ? payload : payload.appointments || [];

      setAppointments(list);
      refreshRecruiterOptions(list);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      showToast(handleApiError(error, t('appointments.calendar.error.fetch', 'Unable to load appointments')), 'error');
    } finally {
      setLoading(false);
    }
  }, [calendarRange.from, calendarRange.to, refreshRecruiterOptions, showToast, t, user?.role, userFilter]);

  const fetchVideoSettings = useCallback(async () => {
    if (user?.role !== 'admin') {
      setVideoSettings(null);
      return;
    }

    try {
      const response = await appointmentsAPI.getVideoConferenceSettings();
      setVideoSettings(response.data || null);
    } catch (error) {
      setVideoSettings(null);
    }
  }, [user?.role]);

  const fetchBookingUrl = useCallback(async () => {
    try {
      const response = await appointmentsAPI.getBookingSettings();
      const slug = response.data?.booking_url_slug;
      if (slug) {
        setBookingUrl(`${window.location.origin}/rdv/${slug}`);
      } else {
        setBookingUrl('');
      }
    } catch (error) {
      setBookingUrl('');
    }
  }, []);

  useEffect(() => {
    fetchVideoSettings();
    fetchBookingUrl();
  }, [fetchBookingUrl, fetchVideoSettings]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const updateAppointmentInState = (appointmentId, updater) => {
    setAppointments((prev) =>
      prev.map((item) => {
        if (item.id !== appointmentId) return item;
        return typeof updater === 'function' ? updater(item) : { ...item, ...updater };
      })
    );
  };

  const handleConfirm = async (appointment, manualUrl = null) => {
    if (appointment.meeting_type === 'online' && (!videoSettings || !videoSettings.is_active)) {
      if (!manualUrl && !showManualUrlInput) {
        setShowManualUrlInput(true);
        return;
      }

      if (!manualUrl) {
        showToast(t('appointments.calendar.error.urlRequired', 'Please provide a meeting URL'), 'error');
        return;
      }
    }

    try {
      setConfirmingId(appointment.id);

      let meetingUrl = null;
      let meetingProvider = null;

      if (appointment.meeting_type === 'online' && videoSettings?.is_active && !manualUrl) {
        const meetingResponse = await appointmentsAPI.generateVideoMeeting({
          appointment_id: appointment.id,
          duration: 60,
          topic: appointment.subject || `Meeting with ${appointment.candidate_name}`
        });
        meetingUrl = meetingResponse.data.meeting_url;
        meetingProvider = meetingResponse.data.provider;
      }

      await appointmentsAPI.confirmAppointment(appointment.id, {
        meeting_url: manualUrl || meetingUrl,
        meeting_provider: manualUrl ? 'manual' : meetingProvider,
        manual_meeting_url: manualUrl
      });

      updateAppointmentInState(appointment.id, {
        status: 'confirmed',
        meeting_url: manualUrl || meetingUrl,
        meeting_provider: manualUrl ? 'manual' : meetingProvider
      });

      showToast(t('appointments.calendar.success.confirm', 'Appointment confirmed successfully'), 'success');
      setSelectedAppointment((prev) =>
        prev && prev.id === appointment.id
          ? {
              ...prev,
              status: 'confirmed',
              meeting_url: manualUrl || meetingUrl,
              meeting_provider: manualUrl ? 'manual' : meetingProvider
            }
          : prev
      );
      setShowManualUrlInput(false);
      setManualMeetingUrl('');
    } catch (error) {
      showToast(handleApiError(error, t('appointments.calendar.error.confirm', 'Error confirming appointment')), 'error');
    } finally {
      setConfirmingId(null);
    }
  };

  const handleCancel = async (appointment, reason) => {
    try {
      setCancellingId(appointment.id);

      await appointmentsAPI.cancelAppointment(appointment.id, {
        cancellation_reason: reason
      });

      updateAppointmentInState(appointment.id, {
        status: 'cancelled',
        cancellation_reason: reason
      });

      showToast(t('appointments.calendar.success.cancel', 'Appointment cancelled'), 'success');
      setSelectedAppointment((prev) =>
        prev && prev.id === appointment.id
          ? { ...prev, status: 'cancelled', cancellation_reason: reason }
          : prev
      );
    } catch (error) {
      showToast(handleApiError(error, t('appointments.calendar.error.cancel', 'Error cancelling appointment')), 'error');
    } finally {
      setCancellingId(null);
    }
  };

  const handleEventClick = (clickInfo) => {
    const appointment = clickInfo.event.extendedProps?.raw;
    if (!appointment) return;
    setSelectedAppointment(appointment);
    setIsEditingDetails(false);
    setEditDraft({
      subject: appointment.subject || '',
      date: appointment.date || '',
      start_time: normalizeTime(appointment.start_time),
      end_time: normalizeTime(appointment.end_time),
      meeting_type: appointment.meeting_type || 'online',
      meeting_url: appointment.meeting_url || ''
    });
    setShowManualUrlInput(false);
    setManualMeetingUrl('');
  };

  const handleSaveDetails = async () => {
    if (!selectedAppointment?.id) return;

    if (!editDraft.date || !editDraft.start_time || !editDraft.end_time) {
      showToast(t('appointments.calendar.error.invalidData', 'Please complete date and time'), 'error');
      return;
    }

    try {
      setUpdatingId(selectedAppointment.id);
      const payload = {
        subject: editDraft.subject || null,
        date: editDraft.date,
        start_time: editDraft.start_time,
        end_time: editDraft.end_time,
        meeting_type: editDraft.meeting_type,
        meeting_url: editDraft.meeting_type === 'online' ? (editDraft.meeting_url || null) : null
      };

      const response = await appointmentsAPI.updateAppointment(selectedAppointment.id, payload);
      const updated = response.data?.appointment || {};

      updateAppointmentInState(selectedAppointment.id, (item) => ({
        ...item,
        ...updated,
        date: updated.date || payload.date,
        start_time: normalizeTime(updated.start_time || payload.start_time),
        end_time: normalizeTime(updated.end_time || payload.end_time),
        subject: updated.subject ?? payload.subject,
        meeting_type: updated.meeting_type || payload.meeting_type,
        meeting_url: updated.meeting_url ?? payload.meeting_url
      }));

      setSelectedAppointment((prev) =>
        prev
          ? {
              ...prev,
              ...updated,
              date: updated.date || payload.date,
              start_time: normalizeTime(updated.start_time || payload.start_time),
              end_time: normalizeTime(updated.end_time || payload.end_time),
              subject: updated.subject ?? payload.subject,
              meeting_type: updated.meeting_type || payload.meeting_type,
              meeting_url: updated.meeting_url ?? payload.meeting_url
            }
          : prev
      );

      setIsEditingDetails(false);
      showToast(t('appointments.calendar.success.update', 'Appointment updated'), 'success');
    } catch (error) {
      showToast(handleApiError(error, t('appointments.calendar.error.update', 'Unable to update appointment')), 'error');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleEventReschedule = async (interactionInfo) => {
    const event = interactionInfo.event;
    const appointment = event.extendedProps?.raw;

    if (!appointment?.id) {
      interactionInfo.revert();
      return;
    }

    try {
      setReschedulingId(appointment.id);
      const nextStart = event.start;
      const fallbackEnd = addMinutes(nextStart, toMinutesDuration(appointment.start_time, appointment.end_time));
      const nextEnd = event.end || fallbackEnd;

      const payload = {
        date: format(nextStart, 'yyyy-MM-dd'),
        start_time: format(nextStart, 'HH:mm'),
        end_time: format(nextEnd, 'HH:mm')
      };

      const response = await appointmentsAPI.rescheduleAppointment(appointment.id, payload);
      const updated = response.data?.appointment || null;

      updateAppointmentInState(appointment.id, (item) => ({
        ...item,
        date: updated?.date || payload.date,
        start_time: normalizeTime(updated?.start_time || payload.start_time),
        end_time: normalizeTime(updated?.end_time || payload.end_time),
        updated_at: updated?.updated_at || item.updated_at
      }));

      if (selectedAppointment?.id === appointment.id) {
        setSelectedAppointment((prev) =>
          prev
            ? {
                ...prev,
                date: updated?.date || payload.date,
                start_time: normalizeTime(updated?.start_time || payload.start_time),
                end_time: normalizeTime(updated?.end_time || payload.end_time)
              }
            : prev
        );
      }

      showToast(t('appointments.calendar.success.reschedule', 'Appointment rescheduled'), 'success');
    } catch (error) {
      interactionInfo.revert();
      showToast(handleApiError(error, t('appointments.calendar.error.reschedule', 'Unable to reschedule appointment')), 'error');
    } finally {
      setReschedulingId(null);
    }
  };

  const onDatesSet = (dateInfo) => {
    const endDate = new Date(dateInfo.end);
    endDate.setDate(endDate.getDate() - 1);

    setCalendarTitle(dateInfo.view.title);
    setCalendarView(dateInfo.view.type);
    setCalendarRange({
      from: format(dateInfo.start, 'yyyy-MM-dd'),
      to: format(endDate, 'yyyy-MM-dd')
    });
  };

  const changeView = (viewId) => {
    const api = calendarRef.current?.getApi();
    if (!api) return;
    api.changeView(viewId);
  };

  const goToday = () => {
    const api = calendarRef.current?.getApi();
    if (!api) return;
    api.today();
  };

  const copyBookingUrl = () => {
    if (!bookingUrl) return;
    navigator.clipboard.writeText(bookingUrl);
    showToast(t('appointments.calendar.success.copyUrl', 'Booking URL copied'), 'success');
  };

  return (
    <div className="space-y-6">
      {bookingUrl && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Link className="h-4 w-4 text-indigo-600" />
                <p className="text-sm font-medium text-gray-900">
                  {t('appointments.calendar.bookingUrl', 'Your booking URL')}
                </p>
              </div>
              <p className="text-sm text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2 font-mono break-all">
                {bookingUrl}
              </p>
            </div>
            <button
              onClick={copyBookingUrl}
              className="inline-flex items-center rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:border-indigo-200 hover:bg-indigo-50"
            >
              {t('common.copy', 'Copy')}
            </button>
          </div>
        </div>
      )}

      <RecentInvitations showToast={showToast} />

      <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Calendar className="h-5 w-5 text-indigo-600" />
              {calendarTitle || t('appointments.tabs.calendar', 'Calendar')}
            </h2>
            {reschedulingId && (
              <p className="text-xs text-indigo-600 mt-1 inline-flex items-center gap-1">
                <RefreshCw className="h-3 w-3 animate-spin" />
                {t('appointments.calendar.rescheduling', 'Updating appointment...')}
              </p>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {user?.role === 'admin' && (
              <select
                value={userFilter}
                onChange={(e) => setUserFilter(e.target.value)}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
                aria-label={t('appointments.calendar.recruiterFilter', 'Recruiter filter')}
              >
                <option value="all">{t('appointments.calendar.allRecruiters', 'All recruiters')}</option>
                {recruiterOptions.map((option) => (
                  <option key={option.id || option.name} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </select>
            )}

            <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
              <button
                onClick={() => changeView('timeGridDay')}
                className={`px-3 py-2 text-sm font-medium ${calendarView === 'timeGridDay' ? 'bg-indigo-50 text-indigo-700' : 'text-gray-600 hover:bg-gray-50'}`}
              >
                {t('appointments.calendar.views.day', 'Day')}
              </button>
              <button
                onClick={() => changeView('timeGridWeek')}
                className={`px-3 py-2 text-sm font-medium border-l border-gray-200 ${calendarView === 'timeGridWeek' ? 'bg-indigo-50 text-indigo-700' : 'text-gray-600 hover:bg-gray-50'}`}
              >
                {t('appointments.calendar.views.week', 'Week')}
              </button>
              <button
                onClick={() => changeView('dayGridMonth')}
                className={`px-3 py-2 text-sm font-medium border-l border-gray-200 ${calendarView === 'dayGridMonth' ? 'bg-indigo-50 text-indigo-700' : 'text-gray-600 hover:bg-gray-50'}`}
              >
                {t('appointments.calendar.views.month', 'Month')}
              </button>
            </div>

            <button
              onClick={goToday}
              className="inline-flex items-center rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:border-indigo-200 hover:bg-indigo-50"
            >
              {t('appointments.calendar.today', 'Today')}
            </button>

            <button
              onClick={fetchAppointments}
              className="inline-flex items-center rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:border-indigo-200 hover:bg-indigo-50"
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              {t('common.refresh', 'Refresh')}
            </button>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 overflow-hidden p-2">
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="timeGridWeek"
            locale={calendarLocale}
            headerToolbar={false}
            allDaySlot={false}
            nowIndicator
            editable
            eventStartEditable
            eventDurationEditable
            eventDrop={handleEventReschedule}
            eventResize={handleEventReschedule}
            eventClick={handleEventClick}
            datesSet={onDatesSet}
            events={eventList}
            height="auto"
            slotMinTime="07:00:00"
            slotMaxTime="22:00:00"
            slotDuration="00:30:00"
            eventTimeFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
            eventContent={(eventInfo) => {
              const item = eventInfo.event.extendedProps?.raw;
              return (
                <div className="px-1 py-0.5">
                  <div className="text-xs font-semibold truncate">{eventInfo.timeText}</div>
                  <div className="text-xs truncate">{eventInfo.event.title}</div>
                  {item?.meeting_type && (
                    <div className="text-[10px] opacity-80 truncate">
                      {item.meeting_type === 'online'
                        ? t('appointments.calendar.online', 'Online')
                        : t('appointments.calendar.physical', 'In-person')}
                    </div>
                  )}
                </div>
              );
            }}
          />
        </div>
      </div>

      {selectedAppointment && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg border border-gray-200 shadow-xl max-w-xl w-full mx-4 max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('appointments.calendar.appointmentDetails', 'Appointment details')}
                </h3>
                <div className="flex items-center gap-2">
                  {!isEditingDetails && (
                    <button
                      onClick={() => setIsEditingDetails(true)}
                      className="inline-flex items-center rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                    >
                      <Pencil className="h-3.5 w-3.5 mr-1.5" />
                      {t('common.edit', 'Edit')}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setSelectedAppointment(null);
                      setShowManualUrlInput(false);
                      setManualMeetingUrl('');
                      setIsEditingDetails(false);
                    }}
                    className="text-gray-400 hover:text-gray-500"
                    aria-label={t('common.close')}
                  >
                    <XCircle className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {isEditingDetails && (
                <div className="mb-5 p-4 rounded-lg border border-indigo-100 bg-indigo-50/40">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">
                    {t('appointments.calendar.editInline', 'Quick edit')}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {t('common.subject', 'Subject')}
                      </label>
                      <input
                        type="text"
                        value={editDraft.subject}
                        onChange={(event) =>
                          setEditDraft((prev) => ({ ...prev, subject: event.target.value }))
                        }
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {t('common.date', 'Date')}
                      </label>
                      <input
                        type="date"
                        value={editDraft.date}
                        onChange={(event) =>
                          setEditDraft((prev) => ({ ...prev, date: event.target.value }))
                        }
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {t('appointments.time', 'Time')}
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="time"
                          value={editDraft.start_time}
                          onChange={(event) =>
                            setEditDraft((prev) => ({ ...prev, start_time: event.target.value }))
                          }
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                        />
                        <input
                          type="time"
                          value={editDraft.end_time}
                          onChange={(event) =>
                            setEditDraft((prev) => ({ ...prev, end_time: event.target.value }))
                          }
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {t('appointments.calendar.meetingType', 'Meeting type')}
                      </label>
                      <select
                        value={editDraft.meeting_type}
                        onChange={(event) =>
                          setEditDraft((prev) => ({ ...prev, meeting_type: event.target.value }))
                        }
                        disabled={selectedAppointment?.meeting_type_locked}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100"
                      >
                        <option value="online">{t('appointments.calendar.online', 'Online')}</option>
                        <option value="physical">{t('appointments.calendar.physical', 'In-person')}</option>
                      </select>
                    </div>

                    {editDraft.meeting_type === 'online' && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          {t('appointments.calendar.meetingUrl', 'Meeting URL')}
                        </label>
                        <input
                          type="url"
                          value={editDraft.meeting_url}
                          onChange={(event) =>
                            setEditDraft((prev) => ({ ...prev, meeting_url: event.target.value }))
                          }
                          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                          placeholder="https://..."
                        />
                      </div>
                    )}
                  </div>

                  <div className="mt-4 flex justify-end gap-2">
                    <button
                      onClick={() => {
                        setIsEditingDetails(false);
                        setEditDraft({
                          subject: selectedAppointment.subject || '',
                          date: selectedAppointment.date || '',
                          start_time: normalizeTime(selectedAppointment.start_time),
                          end_time: normalizeTime(selectedAppointment.end_time),
                          meeting_type: selectedAppointment.meeting_type || 'online',
                          meeting_url: selectedAppointment.meeting_url || ''
                        });
                      }}
                      className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      {t('common.cancel', 'Cancel')}
                    </button>
                    <button
                      onClick={handleSaveDetails}
                      disabled={updatingId === selectedAppointment.id}
                      className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {updatingId === selectedAppointment.id
                        ? t('common.saving', 'Saving...')
                        : t('common.save', 'Save')}
                    </button>
                  </div>
                </div>
              )}

              <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium mb-4 ${getStatusBadgeClasses(selectedAppointment.status)}`}>
                {selectedAppointment.status === 'confirmed' ? <CheckCircle className="h-4 w-4 mr-1" /> : <Clock className="h-4 w-4 mr-1" />}
                {t(`appointments.status.${selectedAppointment.status}`, selectedAppointment.status)}
              </div>

              <div className="space-y-3 text-sm">
                <div className="flex items-start">
                  <Calendar className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">
                      {format(parseLocalDate(selectedAppointment.date), 'EEEE d MMMM yyyy', { locale: dateLocale })}
                    </p>
                    <p className="text-gray-600">{normalizeTime(selectedAppointment.start_time)} - {normalizeTime(selectedAppointment.end_time)}</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <User className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
                  <p className="text-gray-900">{selectedAppointment.candidate_name}</p>
                </div>

                {selectedAppointment.candidate_email && (
                  <div className="flex items-start">
                    <Mail className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
                    <a href={`mailto:${selectedAppointment.candidate_email}`} className="text-indigo-600 hover:text-indigo-700">
                      {selectedAppointment.candidate_email}
                    </a>
                  </div>
                )}

                {selectedAppointment.candidate_phone && (
                  <div className="flex items-start">
                    <Phone className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
                    <a href={`tel:${selectedAppointment.candidate_phone}`} className="text-indigo-600 hover:text-indigo-700">
                      {selectedAppointment.candidate_phone}
                    </a>
                  </div>
                )}

                {selectedAppointment.subject && (
                  <div className="flex items-start">
                    <FileText className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
                    <p className="text-gray-900">{selectedAppointment.subject}</p>
                  </div>
                )}

                <div className="flex items-start">
                  {selectedAppointment.meeting_type === 'online' ? (
                    <Video className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
                  ) : (
                    <MapPin className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
                  )}
                  <p className="text-gray-700">
                    {selectedAppointment.meeting_type === 'online'
                      ? t('appointments.calendar.online', 'Online')
                      : t('appointments.calendar.physical', 'In-person')}
                  </p>
                </div>

                {selectedAppointment.meeting_url && (
                  <div className="flex items-start">
                    <Video className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
                    <a
                      href={selectedAppointment.meeting_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-600 hover:text-indigo-700"
                    >
                      {t('appointments.calendar.joinMeeting', 'Join meeting')}
                    </a>
                  </div>
                )}

                {selectedAppointment.cancellation_reason && (
                  <div className="flex items-start">
                    <AlertTriangle className="h-4 w-4 text-red-500 mr-2 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-900">{t('appointments.calendar.cancellationReason', 'Cancellation reason')}</p>
                      <p className="text-gray-600">{selectedAppointment.cancellation_reason}</p>
                    </div>
                  </div>
                )}
              </div>

              {selectedAppointment.status === 'pending' && showManualUrlInput && selectedAppointment.meeting_type === 'online' && (
                <div className="mt-5 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm font-medium text-amber-900 mb-2">
                    {t('appointments.calendar.manualUrlRequired', 'Please provide the meeting URL')}
                  </p>
                  <input
                    type="url"
                    value={manualMeetingUrl}
                    onChange={(e) => setManualMeetingUrl(e.target.value)}
                    placeholder="https://meet.google.com/abc-defg-hij"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              )}

              {selectedAppointment.status === 'pending' && (
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    onClick={() => {
                      if (showManualUrlInput) {
                        handleConfirm(selectedAppointment, manualMeetingUrl);
                      } else {
                        handleConfirm(selectedAppointment);
                      }
                    }}
                    disabled={confirmingId === selectedAppointment.id || (showManualUrlInput && !manualMeetingUrl)}
                    className="inline-flex justify-center items-center rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                  >
                    {confirmingId === selectedAppointment.id
                      ? t('common.confirming', 'Confirming...')
                      : t('appointments.calendar.confirm', 'Confirm')}
                  </button>
                  <button
                    onClick={async () => {
                      const reason = await prompt({
                        title: t('appointments.calendar.cancelAppointment', 'Cancel appointment'),
                        message: t('appointments.calendar.cancelReasonMessage', 'Optional cancellation reason'),
                        placeholder: t('appointments.calendar.cancelReasonPlaceholder', 'Example: Candidate unavailable...'),
                        submitText: t('appointments.calendar.confirmCancel', 'Cancel appointment'),
                        cancelText: t('common.cancel', 'Back')
                      });
                      if (reason !== null) {
                        handleCancel(selectedAppointment, reason || '');
                      }
                    }}
                    disabled={cancellingId === selectedAppointment.id}
                    className="inline-flex justify-center items-center rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                  >
                    {cancellingId === selectedAppointment.id
                      ? t('common.cancelling', 'Cancelling...')
                      : t('appointments.calendar.cancel', 'Cancel')}
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}

      <PromptModal {...promptProps} />
    </div>
  );
};

function toMinutesDuration(start, end) {
  const startMin = parseTimeToMinutes(start);
  const endMin = parseTimeToMinutes(end);
  if (endMin <= startMin) {
    return 30;
  }
  return endMin - startMin;
}

function parseTimeToMinutes(timeValue) {
  const normalized = normalizeTime(timeValue);
  const [hours, minutes] = normalized.split(':').map(Number);
  return (hours * 60) + minutes;
}

export default MyCalendar;
